// Shared design tokens for the CP Sourcing screens (palette mirrors the Leads
// tab — see the Sources note below). Kept in its own module so
// CpSourcingMain, MyCallsView and CpDetailDrawer can all import it
// without circular-import problems.
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import PersonOffIcon from "@mui/icons-material/PersonOff";

// Design tokens, mapped onto the Leads tab's actual values so CP Sourcing reads
// as the same product. Key names are unchanged because six other CP Sourcing
// files import them.
//
// Sources: Leadcard.module.css, Leadcard.jsx, Leads.module.css, LeadStatusChip.jsx.
export const C = {
  // Primary action colour — Leads' button/link blue (Leads.jsx:1508).
  primary: "#1570EF",

  // `indigo*` keys are kept for compatibility with existing call sites, but now
  // carry Leads' blue so selected/active UI matches Leads rather than Tailwind indigo.
  indigo: "#1570EF", indigo2: "#1570EF", indigoSoft: "#EFF8FF", indigoBd: "#84CAFF",

  // Semantic colours, all drawn from Leads' chip palette in Leadcard.jsx.
  green: "#027A48", greenSoft: "#ECFDF3", greenBd: "#ECFDF3",
  red: "#B42318", redSoft: "#FEF3F2", redBd: "#FEF3F2",
  crimson: "#B42318", crimsonSoft: "#FEF3F2", crimsonBd: "#FEF3F2",
  amber: "#B54708", amberSoft: "#FFFAEB", amberBd: "#FFFAEB",
  sky: "#0E7090", skySoft: "#ECFDFF", skyBd: "#ECFDFF",
  slate: "#667085", slateSoft: "#F2F4F7", slateBd: "#EAECF0",

  // Surfaces and borders — Leads uses a white canvas with #EAECF0 hairlines.
  page: "#FFFFFF", white: "#FFFFFF", border: "#EAECF0", border2: "#F2F4F7",

  // Text scale, mapped onto Leads' neutral ramp.
  txt1: "#101828", txt2: "#344054", txt3: "#667085", txt4: "#98A2B3",

  // The app bundles no monospace face, and Leads uses none, so `mono` points at
  // the body font to keep numeric columns in the app typeface.
  sans: "Inter-Medium", mono: "Inter-Medium",
};

// Three-state CP site visit verification palette. Lives here rather than in
// CpSiteVisitApprovalView so the desktop grid's StatusChip and the mobile
// SiteVisitCard pill can both read it without importing each other.
// Colours are Leads' chip palette (Leadcard.jsx); `border` and `dot` are kept
// because CpSiteVisitApprovalView and Mobile/SiteVisitCard read them.
export const statusPalette = (status) =>
  status === "approved"
    ? { bg: "#ECFDF3", color: "#027A48", border: "#ECFDF3", dot: "#027A48", label: "Verified" }
    : status === "rejected"
    ? { bg: "#FEF3F2", color: "#B42318", border: "#FEF3F2", dot: "#B42318", label: "Rejected" }
    : { bg: "#FFFAEB", color: "#B54708", border: "#FFFAEB", dot: "#B54708", label: "Verification Pending" };

export const TIER_META = {
  all:         { label: "All My CPs",   color: "#3538CD", bg: "#EEF4FF", border: "#EEF4FF", sub: "assigned to you",    Icon: PeopleAltIcon },
  outreach:    { label: "Outreach",     color: "#B54708", bg: "#FFFAEB", border: "#FFFAEB", sub: "14–29 days quiet",   Icon: PhoneForwardedIcon },
  escalate:    { label: "Escalate",     color: "#C01048", bg: "#FFF1F3", border: "#FFF1F3", sub: "30–59 days quiet",   Icon: ReportProblemIcon },
  critical:    { label: "Critical",     color: "#B42318", bg: "#FEF3F2", border: "#FEF3F2", sub: "60+ days quiet",     Icon: WhatshotIcon },
  neverActive: { label: "Never Active", color: "#667085", bg: "#F2F4F7", border: "#EAECF0", sub: "No site visit ever", Icon: PersonOffIcon },
  active:      { label: "Active",       color: "#027A48", bg: "#ECFDF3", border: "#ECFDF3", sub: "Visited recently",   Icon: PeopleAltIcon },
};
